import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Typography} from 'views/components';
import {Translate, useNavigate} from 'views/module';

import CommunicationPanel from './communication/CommunicationPanel';
import CaseInbox from './list/CaseInbox';
import CaseView from './view/CaseView';

import {getParticipantKind, getParticipantResourceRn, getParticipantRns} from './communication/communicationUtils';
import backend from 'utils/backend';
import moment from 'moment';

const caseStatusFilterValues = ['OPEN', 'PENDING', 'COMPLETE'];
const caseSort = {sortKey: 'updatedAt', sortDirection: 'DESC'};
const caseDateRanges = [
  {key: 'TODAY', days: 1},
  {key: 'LAST_3_DAYS', days: 3},
  {key: 'LAST_7_DAYS', days: 7},
  {key: 'LAST_28_DAYS', days: 28},
];
const caseTypeGroups = [
  {key: 'ALL', caseTypes: []},
  {
    key: 'SALES',
    caseTypes: [
      'LEAD_QUALIFICATION',
    ],
  },
  {
    key: 'RECRUITING',
    caseTypes: [
      'CAREGIVER_JOB_INQUIRY',
      'JOB_POSTING_NOTIFICATION',
      'CAREGIVER_PROFILE_COMPLETION',
    ],
  },
  {
    key: 'OPERATIONS',
    caseTypes: [
      'MISSING_PAY',
      'CAREGIVER_FAMILY_CHAT_MONITORING',
      'CAREGIVER_SHIFT_CANCELLATION',
      'TIMECLOCK',
      'CAREGIVER_RESCHEDULE_REQUEST',
      'CAREGIVER_DISTRESS_MONITORING',
    ],
  },
];

const getCaseUpdatedAt = (caseData) => {
  const updatedAtTime = new Date(caseData.updatedAt).getTime();

  return Number.isNaN(updatedAtTime) ? 0 : updatedAtTime;
};

const getCasePriority = (caseData) => {
  if (caseData.priority === null || caseData.priority === undefined || caseData.priority === '') {
    return Number.MAX_SAFE_INTEGER;
  }

  const priority = Number(caseData.priority);

  if (Number.isFinite(priority)) {
    return priority;
  }

  return Number.MAX_SAFE_INTEGER;
};

const sortCasesByPriority = (caseItems) => {
  return [...caseItems].sort((firstCase, secondCase) => {
    const priorityDifference = getCasePriority(firstCase) - getCasePriority(secondCase);

    if (priorityDifference !== 0) {
      return priorityDifference;
    }

    return getCaseUpdatedAt(secondCase) - getCaseUpdatedAt(firstCase);
  });
};

const mergeCasesByRn = (currentCases, nextCases) => {
  const mergedCases = [...currentCases];
  const caseIndexByRn = {};
  mergedCases.forEach((caseData, index) => {
    caseIndexByRn[caseData.caseRn] = index;
  });

  nextCases.forEach((caseData) => {
    if (!caseData?.caseRn) return;

    const existingIndex = caseIndexByRn[caseData.caseRn];

    if (existingIndex === undefined) {
      caseIndexByRn[caseData.caseRn] = mergedCases.length;
      mergedCases.push(caseData);
      return;
    }

    mergedCases[existingIndex] = {
      ...mergedCases[existingIndex],
      ...caseData,
    };
  });

  return mergedCases;
};

const CaseScene = (props) => {
  const {
    selectedCaseRn,
    statusProgressWrap: progressWrap,
  } = props;
  const navigate = useNavigate();
  const [cases, setCases] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState();
  const [dateRangeKey, setDateRangeKey] = React.useState('TODAY');
  const [caseTypeGroupKey, setCaseTypeGroupKey] = React.useState('ALL');
  const [pendingOnly, setPendingOnly] = React.useState(false);
  const [selectedCaseDetail, setSelectedCaseDetail] = React.useState(null);
  const [participantDetails, setParticipantDetails] = React.useState({});
  const [casesLoadFailed, setCasesLoadFailed] = React.useState(false);
  const [stage, setStage] = React.useState('FILL');
  const requestIdRef = React.useRef(0);
  const participantDetailsLoadingRef = React.useRef(new Set());

  const isLoading = stage === 'LOADING';
  const selectedDateRangeStart = React.useMemo(() => {
    const selectedDateRange = caseDateRanges.find((dateRange) => dateRange.key === dateRangeKey) || caseDateRanges[2];

    return moment().subtract(selectedDateRange.days - 1, 'days').startOf('day').toISOString();
  }, [dateRangeKey]);
  const caseFilters = React.useMemo(() => {
    const selectedCaseTypeGroup = caseTypeGroups.find((caseTypeGroup) => caseTypeGroup.key === caseTypeGroupKey);
    const filters = [{
      key: 'status',
      value: pendingOnly ? ['PENDING'] : caseStatusFilterValues,
    }];

    if (selectedCaseTypeGroup?.caseTypes?.length) {
      filters.push({
        key: 'caseType',
        value: selectedCaseTypeGroup.caseTypes,
      });
    }

    return filters;
  }, [caseTypeGroupKey, pendingOnly]);
  const sortedCases = React.useMemo(() => {
    return sortCasesByPriority(cases);
  }, [cases]);
  const visibleCases = React.useMemo(() => {
    if (
      !selectedCaseDetail?.caseRn ||
      selectedCaseDetail.caseRn !== selectedCaseRn ||
      (pendingOnly && selectedCaseDetail.status !== 'PENDING') ||
      cases.some((caseData) => caseData.caseRn === selectedCaseDetail.caseRn)
    ) {
      return sortedCases;
    }

    return [
      selectedCaseDetail,
      ...sortedCases,
    ];
  }, [cases, pendingOnly, selectedCaseDetail, selectedCaseRn, sortedCases]);
  const selectedCaseSummary = React.useMemo(() => {
    return visibleCases.find((caseData) => caseData.caseRn === selectedCaseRn) || null;
  }, [selectedCaseRn, visibleCases]);
  const selectedCaseData = React.useMemo(() => {
    if (selectedCaseDetail?.caseRn === selectedCaseRn) {
      return selectedCaseDetail;
    }

    return selectedCaseSummary;
  }, [selectedCaseDetail, selectedCaseRn, selectedCaseSummary]);
  const selectedParticipantRns = React.useMemo(() => {
    return [...new Set(getParticipantRns(selectedCaseData))];
  }, [selectedCaseData]);
  const selectedParticipantResourceRns = React.useMemo(() => {
    return [...new Set(selectedParticipantRns.map((participantRn) => getParticipantResourceRn(participantRn)).filter(Boolean))];
  }, [selectedParticipantRns]);
  const onSelectedCaseDataChange = React.useCallback((nextCaseData) => {
    setSelectedCaseDetail(nextCaseData);

    if (!nextCaseData?.caseRn) return;

    setCases((currentCases) => {
      if (!currentCases.some((caseData) => caseData.caseRn === nextCaseData.caseRn)) {
        return currentCases;
      }

      return currentCases.map((caseData) => (
        caseData.caseRn === nextCaseData.caseRn ?
          {
            ...caseData,
            ...nextCaseData,
          } :
          caseData
      ));
    });
  }, []);
  const selectCaseRn = React.useCallback((caseRn, options={}) => {
    if (caseRn) {
      navigate(`/case/${encodeURIComponent(caseRn)}`, {replace: Boolean(options.replace)});
      return;
    }

    navigate('/cases', {replace: Boolean(options.replace)});
  }, [navigate]);

  const loadCases = React.useCallback(async (
      date,
      startKey,
      shouldReset = false,
      requestId = requestIdRef.current,
      showProgress = true,
  ) => {
    await progressWrap(async () => {
      try {
        const caseResponse = await backend.case.queryCaseFromDate(date, caseFilters, caseSort, startKey);

        if (requestId !== requestIdRef.current) return;

        setCases((currentCases) => {
          const nextCases = caseResponse?.list || [];
          return shouldReset ? mergeCasesByRn([], nextCases) : mergeCasesByRn(currentCases, nextCases);
        });
        setLastEvaluatedKey(caseResponse?.lastEvaluatedKey);
        setCasesLoadFailed(false);
      } catch {
        if (requestId !== requestIdRef.current) return;

        if (shouldReset) {
          setCases([]);
          setLastEvaluatedKey();
        }
        setCasesLoadFailed(true);
      }
    }, {setStage, showProgress});
  }, [caseFilters, progressWrap]);

  React.useEffect(() => {
    if (selectedCaseRn || sortedCases.length === 0) return;

    selectCaseRn(visibleCases[0].caseRn, {replace: true});
  }, [selectCaseRn, selectedCaseRn, sortedCases, visibleCases]);

  React.useEffect(() => {
    setSelectedCaseDetail(null);
  }, [selectedCaseRn]);

  React.useEffect(() => {
    const participantResourceRnsToLoad = selectedParticipantResourceRns.filter((participantResourceRn) => {
      return !participantDetails[participantResourceRn] && !participantDetailsLoadingRef.current.has(participantResourceRn);
    });

    if (participantResourceRnsToLoad.length === 0) return;

    participantResourceRnsToLoad.forEach((participantResourceRn) => {
      participantDetailsLoadingRef.current.add(participantResourceRn);
    });

    const loadParticipantDetails = async () => {
      const nextParticipantDetails = await Promise.all(participantResourceRnsToLoad.map(async (participantResourceRn) => {
        const participantKind = getParticipantKind(participantResourceRn);

        if (!['CLIENT', 'CAREGIVER'].includes(participantKind)) {
          return {
            participantRn: participantResourceRn,
            detail: null,
            loadFailed: false,
          };
        }

        try {
          const output = participantKind === 'CLIENT' ?
            await backend.client.getClient(participantResourceRn) :
            await backend.caregiver.getCaregiver(participantResourceRn);

          return {
            participantRn: participantResourceRn,
            detail: participantKind === 'CLIENT' ? output?.clientDetail : output?.caregiverDetail,
            loadFailed: false,
          };
        } catch {
          return {
            participantRn: participantResourceRn,
            detail: null,
            loadFailed: true,
          };
        }
      }));

      participantResourceRnsToLoad.forEach((participantResourceRn) => {
        participantDetailsLoadingRef.current.delete(participantResourceRn);
      });

      setParticipantDetails((currentParticipantDetails) => {
        return nextParticipantDetails.reduce((result, participantDetail) => {
          return {
            ...result,
            [participantDetail.participantRn]: {
              detail: participantDetail.detail,
              loadFailed: participantDetail.loadFailed,
            },
          };
        }, currentParticipantDetails);
      });
    };

    loadParticipantDetails();
  }, [participantDetails, selectedParticipantResourceRns]);

  React.useEffect(() => {
    requestIdRef.current += 1;
    setCases([]);
    setLastEvaluatedKey();
    setCasesLoadFailed(false);
    loadCases(selectedDateRangeStart, null, true, requestIdRef.current);

    return () => {
      requestIdRef.current += 1;
    };
  }, [caseTypeGroupKey, loadCases, selectedDateRangeStart]);

  const loadMoreCases = () => {
    if (!lastEvaluatedKey || isLoading) return;
    loadCases(selectedDateRangeStart, lastEvaluatedKey, false, requestIdRef.current, false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: {
          xs: 'column',
          md: 'row',
        },
        width: '100%',
        height: {
          md: '97vh',
        },
        minHeight: {
          md: '97vh',
        },
        overflow: {
          md: 'hidden',
        },
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #ECECF1',
      }}
    >
      <CaseInbox
        casesLoadFailed={casesLoadFailed}
        caseDateRanges={caseDateRanges}
        caseTypeGroupKey={caseTypeGroupKey}
        caseTypeGroups={caseTypeGroups}
        dateRangeKey={dateRangeKey}
        isLoading={isLoading}
        lastEvaluatedKey={lastEvaluatedKey}
        loadMoreCases={loadMoreCases}
        pendingOnly={pendingOnly}
        selectedCaseRn={selectedCaseRn}
        setCaseTypeGroupKey={setCaseTypeGroupKey}
        setDateRangeKey={setDateRangeKey}
        setPendingOnly={setPendingOnly}
        selectCaseRn={selectCaseRn}
        visibleCases={visibleCases}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: {
            xs: '480px',
            md: 0,
          },
        }}
      >
        {
          selectedCaseRn ? (
            <CaseView
              caseRn={selectedCaseRn}
              isEmbedded
              onCaseDataChange={onSelectedCaseDataChange}
              participantDetails={participantDetails}
            />
          ) : (
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: '#6D7280',
                px: {
                  xs: 2,
                  md: 2,
                  lg: 3,
                },
                py: 3,
              }}
            >
              <Translate expression='case.List.NoCaseSelected' />
            </Typography>
          )
        }
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          width: {
            xs: '100%',
            md: '364px',
            lg: '364px',
          },
          borderLeft: {
            md: '1px solid #ECECF1',
          },
          minHeight: {
            xs: '320px',
            md: 0,
          },
        }}
      >
        <CommunicationPanel
          caseData={selectedCaseData}
          participantDetails={participantDetails}
        />
      </Box>
    </Box>
  );
};

CaseScene.propTypes = {
  selectedCaseRn: PropTypes.string,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(CaseScene));
